// The 3D table: sky and weather that follow the state of the game, the sea, the ship and
// its pieces, plus one-shot effects driven by game events (surges, explosions, splashes…).
import { OrbitControls, Sky } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

import * as uf from "../../../../../common/unfathomable";
import { isWater, kDeckY, kDeepXZ, monsterAnchor, spaceCentre, toXZ } from "./coords";
import { Burst, Rain } from "./fx";
import { Ocean, type OceanLook } from "./Ocean";
import { Pieces } from "./Pieces";
import { Ship, ShipBody } from "./Ship";
import { BreakFire, ShipHalf, useWreckClock, WreckFx } from "./Wreck";

// ---------------------------------------------------------------- mood
export interface Mood {
  storm: number;
  win: boolean;
  lose: boolean;
  /** The ship explodes, breaks in two and sinks (a lost game). */
  wreck?: boolean;
}

/** A forced mood (animation debugging). */
export type MoodOverride = Mood;

export function moodOf(view: uf.GameView): Mood {
  if (view.gameOver) return { storm: view.gameOver.humansWin ? 0 : 1, win: view.gameOver.humansWin, lose: !view.gameOver.humansWin, wreck: !view.gameOver.humansWin };
  const minRes = Math.min(...Object.values(view.resources));
  let s = 0.12 + view.ritual * 0.05;
  if (view.awakeningDone) s += 0.25;
  if (view.hydra !== "deep") s += 0.15;
  if (view.dagon !== "deep") s += 0.15;
  if (minRes <= 3) s += 0.15;
  if (minRes <= 1) s += 0.2;
  return { storm: Math.min(1, s), win: false, lose: false };
}

const C = (h: string) => new THREE.Color(h);
const kCalm = { deep: C("#0f3d4c"), shallow: C("#35909a"), sky: C("#e0b48a"), sun: C("#ffd79a"), fog: C("#8a8079") };
const kStorm = { deep: C("#04121a"), shallow: C("#163238"), sky: C("#39434c"), sun: C("#7f8fa0"), fog: C("#1d2428") };
const kWin = { deep: C("#0b4566"), shallow: C("#42b0bf"), sky: C("#a8d4ff"), sun: C("#fff4d6"), fog: C("#bcd6e8") };

function lookOf(m: Mood) {
  const pick = (k: keyof typeof kCalm) => (m.win ? kWin[k].clone() : kCalm[k].clone().lerp(kStorm[k], m.storm));
  const sunElev = m.win ? 0.75 : 0.18 - m.storm * 0.1;
  return {
    ocean: {
      storm: m.storm, deep: pick("deep"), shallow: pick("shallow"), sky: pick("sky"), sunColor: pick("sun"),
      sunDir: new THREE.Vector3(-0.6, sunElev, -0.7).normalize(),
    } as OceanLook,
    fog: pick("fog"),
    fogFar: m.win ? 260 : 170 - m.storm * 110,
    sunPos: new THREE.Vector3(-60, sunElev * 100, -70),
  };
}

// ---------------------------------------------------------------- effects from events
type Fx =
  | { key: number; kind: "burst"; pos: [number, number, number]; color: string; spread?: number; up?: number; size?: number; life?: number; count?: number; additive?: boolean }
  | { key: number; kind: "pillar"; pos: [number, number, number] };

function RitualPillar({ pos }: { pos: [number, number, number] }) {
  const ref = useRef<THREE.Mesh>(null);
  const t = useRef(0);
  useFrame((_, dt) => {
    t.current += dt;
    const m = ref.current;
    if (!m) return;
    const k = t.current / 2.4;
    m.scale.set(1 + k * 0.6, Math.min(1, t.current * 2), 1 + k * 0.6);
    (m.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.75 * (1 - k));
  });
  return (
    <mesh ref={ref} position={[pos[0], pos[1] + 6, pos[2]]}>
      <cylinderGeometry args={[0.7, 1.2, 12, 32, 1, true]} />
      <meshBasicMaterial color="#c38cff" transparent opacity={0.7} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} />
    </mesh>
  );
}

interface Impulses {
  flow: React.MutableRefObject<number>;
  flash: React.MutableRefObject<number>;
  shake: React.MutableRefObject<number>;
  /** Incremented to strike a lightning bolt. */
  bolt: React.MutableRefObject<number>;
}

// ---------------------------------------------------------------- lightning
function boltPoints(rnd: () => number): THREE.Vector3[] {
  const x0 = (rnd() - 0.5) * 120, z0 = -45 - rnd() * 50;
  const pts: THREE.Vector3[] = [];
  let x = x0, z = z0;
  const segs = 14;
  for (let i = 0; i <= segs; i++) {
    const y = 55 * (1 - i / segs);
    pts.push(new THREE.Vector3(x, y, z));
    x += (rnd() - 0.5) * 6; z += (rnd() - 0.5) * 3;
  }
  return pts;
}

/** Jagged forked bolts far over the sea; each strike flickers for ~0.4 s. */
function Lightning({ imp }: { imp: Impulses }) {
  const [main, fork] = useMemo(() => {
    const mat = new THREE.LineBasicMaterial({ color: "#e8f0ff", transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending, fog: false });
    const make = () => { const l = new THREE.Line(new THREE.BufferGeometry(), mat); l.visible = false; l.frustumCulled = false; return l; };
    return [make(), make()];
  }, []);
  const seen = useRef(imp.bolt.current);
  const t = useRef(9);
  useFrame((_, dt) => {
    if (imp.bolt.current !== seen.current) {
      seen.current = imp.bolt.current;
      t.current = 0;
      const pts = boltPoints(Math.random);
      main.geometry.setFromPoints(pts);
      const from = pts[4 + Math.floor(Math.random() * 5)];
      const forkPts = [from];
      for (let i = 1; i < 6; i++) forkPts.push(forkPts[i - 1].clone().add(new THREE.Vector3((Math.random() - 0.2) * 5, -4.5, (Math.random() - 0.5) * 2)));
      fork.geometry.setFromPoints(forkPts);
    }
    t.current += dt;
    const k = t.current;
    const on = k < 0.08 || (k > 0.14 && k < 0.22) || (k > 0.3 && k < 0.4);
    main.visible = fork.visible = on;
  });
  return (
    <>
      <primitive object={main} />
      <primitive object={fork} />
    </>
  );
}

function strike(imp: Impulses) {
  imp.bolt.current++;
  imp.flash.current = 1;
}

/** Turn fresh game events into scene effects and impulses. */
function useEventFx(view: uf.GameView, imp: Impulses) {
  const [fx, setFx] = useState<Fx[]>([]);
  const seen = useRef<number>(view.events.at(-1)?.seq ?? 0);
  const lastTrack = useRef({ travel: view.travel, ritual: view.ritual });
  useEffect(() => {
    const fresh = view.events.filter(e => e.seq > seen.current);
    if (fresh.length === 0) return;
    seen.current = fresh[fresh.length - 1].seq;
    const add: Fx[] = [];
    for (const { seq, ev } of fresh) {
      switch (ev.type) {
        case "track":
          if (ev.track === "travel" && ev.value > lastTrack.current.travel) {
            imp.flow.current = 9;
            const [x, z] = toXZ(1960, 917);
            add.push({ key: seq, kind: "burst", pos: [x + 1, 0.3, z], color: "#e6f4f1", spread: 2.5, up: 1.5, size: 1.4, life: 1.8 });
          }
          if (ev.track === "ritual" && ev.value > lastTrack.current.ritual) {
            const [x, z] = spaceCentre("chapel");
            add.push({ key: seq, kind: "pillar", pos: [x, kDeckY, z] });
          }
          lastTrack.current = { ...lastTrack.current, [ev.track]: ev.value };
          break;
        case "damageDrawn": {
          // Non-structural damage: a visible effect where it happens (structural damage is handled by "damage").
          const at = (sp: uf.SpaceId, y = kDeckY + 0.3): [number, number, number] => { const [x, z] = spaceCentre(sp); return [x, y, z]; };
          if (ev.card === "fuelLeak") {
            add.push({ key: seq, kind: "burst", pos: at("boiler"), color: "#14100c", spread: 2.2, up: 6, size: 1.8, life: 2.4, count: 34, additive: false });
            add.push({ key: seq + 0.0001, kind: "burst", pos: at("boiler", kDeckY + 0.1), color: "#3a2a12", spread: 3.5, up: 1.2, size: 1.4, life: 2.2, count: 24, additive: false });
            imp.shake.current = 0.7;
          } else if (ev.card === "floodedFoodStores") {
            add.push({ key: seq, kind: "burst", pos: at("galley"), color: "#8fd3ff", spread: 1.2, up: 9, size: 1.3, life: 2.2, count: 40 });
            add.push({ key: seq + 0.0001, kind: "burst", pos: at("galley", kDeckY + 0.1), color: "#cfeeff", spread: 3.2, up: 1.5, size: 1.1, life: 1.8, count: 26, additive: false });
            imp.shake.current = 0.6;
          } else if (ev.card === "fleeingPassengers") {
            add.push({ key: seq, kind: "burst", pos: at("D8", 0.2), color: "#e6f4f1", spread: 2.6, up: 4, size: 1.2, life: 1.8, count: 30 });
            imp.shake.current = 0.4;
          }
          break;
        }
        case "damage": {
          const [x, z] = spaceCentre(ev.space);
          add.push({ key: seq, kind: "burst", pos: [x, kDeckY + 0.4, z], color: "#ff8a3a", spread: 3, up: 4, size: 1.1 });
          imp.shake.current = 1;
          break;
        }
        case "monster":
          if (ev.from === "deep" && ev.to !== "deep" && ev.to !== "supply") {
            const [x, z] = monsterAnchor(ev.to);
            add.push({ key: seq, kind: "burst", pos: [x, 0.2, z], color: "#bfe9d7", spread: 2.2, up: 5, size: 1.2 });
            if (ev.what !== "deepOne") imp.shake.current = 0.8;
          } else if (ev.from === "supply" && ev.to === "deep") {
            const [x, z] = kDeepXZ;
            add.push({ key: seq, kind: "burst", pos: [x, 0.3, z], color: "#5fd1b0", spread: 1.2, up: 2, size: 0.8 });
          }
          break;
        case "defeat":
          if (ev.space) {
            const [x, z] = spaceCentre(ev.space);
            add.push({ key: seq, kind: "burst", pos: [x, kDeckY + 0.6, z], color: ev.target === "deepOne" ? "#7dffb6" : "#ffffff", spread: 1.6, up: 2.5, size: 0.7 });
          }
          break;
        case "awakening":
          strike(imp); imp.shake.current = 0.6;
          break;
        case "banishment": {
          const [x, z] = spaceCentre("chapel");
          add.push({ key: seq, kind: "pillar", pos: [x, kDeckY, z] });
          imp.flash.current = 1;
          break;
        }
      }
    }
    // Track effects compare with the last known value; resync with the real state so faked
    // (debug) events never hide a later real advance.
    lastTrack.current = { travel: view.travel, ritual: view.ritual };
    if (add.length === 0) return;
    setFx(f => [...f, ...add]);
    setTimeout(() => setFx(f => f.filter(x => !add.includes(x))), 2800);
  }, [view.events]); // eslint-disable-line react-hooks/exhaustive-deps
  return fx;
}

// ---------------------------------------------------------------- atmosphere
function Atmosphere({ look, mood, imp }: { look: ReturnType<typeof lookOf>; mood: Mood; imp: Impulses }) {
  const { scene } = useThree();
  const sun = useRef<THREE.DirectionalLight>(null);
  const hemi = useRef<THREE.HemisphereLight>(null);
  const nextBolt = useRef(3);
  useEffect(() => {
    scene.fog = new THREE.Fog(look.fog.clone(), 30, look.fogFar);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useFrame((_, dt) => {
    const fog = scene.fog as THREE.Fog | null;
    const k = Math.min(1, dt * 0.8);
    if (fog) { fog.color.lerp(look.fog, k); fog.far += (look.fogFar - fog.far) * k; }
    // Distant lightning once the weather turns; more often as the storm grows.
    if (mood.storm > 0.25 && !mood.win) {
      nextBolt.current -= dt;
      if (nextBolt.current <= 0) { strike(imp); nextBolt.current = 2.5 + (1 - mood.storm) * 14 + Math.random() * 6; }
    }
    imp.flash.current = Math.max(0, imp.flash.current - dt * 3.5);
    imp.flow.current = Math.max(0, imp.flow.current - dt * 3.5);
    const f = imp.flash.current;
    if (sun.current) sun.current.intensity = (mood.win ? 3.4 : 2.8 - mood.storm * 1.5) + f * 6;
    if (hemi.current) hemi.current.intensity = (mood.win ? 1.6 : 1.3 - mood.storm * 0.55) + f * 2.5;
    if (fog && f > 0) fog.color.lerp(new THREE.Color("#c9d6ff"), f * 0.3);
  });
  return (
    <>
      <Sky distance={4000} sunPosition={look.sunPos} turbidity={mood.win ? 2 : 6 + mood.storm * 14} rayleigh={mood.win ? 1 : 1.6 - mood.storm * 1.4}
        mieCoefficient={0.008} mieDirectionalG={0.85} />
      <hemisphereLight ref={hemi} args={["#e6dccb", "#2a3a40", 1.2]} />
      <ambientLight intensity={0.25} color="#ffd9a8" />
      <directionalLight ref={sun} position={[-18, 26, -12]} intensity={1.6} castShadow shadow-mapSize={[2048, 2048]}
        shadow-camera-left={-18} shadow-camera-right={18} shadow-camera-top={12} shadow-camera-bottom={-12} shadow-camera-near={1} shadow-camera-far={80} shadow-bias={-0.0005} />
      {/* A light drizzle always; heavy rain in a storm. */}
      <Rain amount={mood.win ? 0 : Math.max(0.3, mood.storm)} />
      <Lightning imp={imp} />
    </>
  );
}

/** Shakes the world on impacts (decays quickly). */
function Shaker({ imp, children }: { imp: Impulses; children: React.ReactNode }) {
  const ref = useRef<THREE.Group>(null);
  useFrame(({ clock }, dt) => {
    imp.shake.current = Math.max(0, imp.shake.current - dt * 1.6);
    const s = imp.shake.current * 0.18;
    const t = clock.elapsedTime * 40;
    if (ref.current) ref.current.position.set(Math.sin(t) * s, Math.sin(t * 1.3) * s * 0.5, Math.cos(t * 0.9) * s);
  });
  return <group ref={ref}>{children}</group>;
}

export interface Scene3DProps {
  view: uf.GameView;
  artOk: boolean;
  targets: Map<uf.SpaceId, string>;
  /** A space was clicked (inspect it; clicking never acts). */
  onSpace: (sp: uf.SpaceId) => void;
  moodOverride?: MoodOverride | null;
  /** Changing this number strikes a lightning bolt (debug). */
  bolt?: number;
  /** Changing this number flies the camera back to the default view. */
  viewReset?: number;
  /** The space whose Deep One is activating right now (pulsing ring). */
  actSpace?: uf.SpaceId | null;
}

/** The default camera: high enough to see the whole ship, the Deep and the water around. */
const kCamPos = new THREE.Vector3(0, 19, 20);
const kCamTarget = new THREE.Vector3(0, 0, 0.5);

/** Flies the camera back to the default view whenever `n` changes. */
function CameraReset({ n }: { n: number }) {
  const { camera, controls } = useThree() as unknown as { camera: THREE.Camera; controls: { target: THREE.Vector3; update: () => void } | null };
  const fly = useRef<{ t: number; fromPos: THREE.Vector3; fromTarget: THREE.Vector3 } | null>(null);
  useEffect(() => {
    if (!n || !controls) return;
    fly.current = { t: 0, fromPos: camera.position.clone(), fromTarget: controls.target.clone() };
  }, [n]); // eslint-disable-line react-hooks/exhaustive-deps
  useFrame((_, dt) => {
    const f = fly.current;
    if (!f || !controls) return;
    f.t = Math.min(1, f.t + dt / 0.9);
    const e = f.t < 0.5 ? 4 * f.t ** 3 : 1 - (-2 * f.t + 2) ** 3 / 2;
    camera.position.lerpVectors(f.fromPos, kCamPos, e);
    controls.target.lerpVectors(f.fromTarget, kCamTarget, e);
    controls.update();
    if (f.t >= 1) fly.current = null;
  });
  return null;
}

function World({ view, artOk, targets, onSpace, moodOverride, bolt, actSpace }: Omit<Scene3DProps, "viewReset">) {
  const mood = useMemo(() => moodOverride ?? moodOf(view), [view, moodOverride]);
  const look = useMemo(() => lookOf(mood), [mood]);
  const imp: Impulses = { flow: useRef(0), flash: useRef(0), shake: useRef(0), bolt: useRef(0) };
  const fx = useEventFx(view, imp);
  useEffect(() => { if (bolt) strike(imp); }, [bolt]); // eslint-disable-line react-hooks/exhaustive-deps
  const wrecking = !!mood.wreck;
  const { clock: wreck, ship } = useWreckClock(wrecking);
  const rock = useRef<THREE.Group>(null);
  return (
    <>
      <Atmosphere look={look} mood={mood} imp={imp} />
      <Shaker imp={imp}>
        <Ocean look={look.ocean} flowRef={imp.flow} flashRef={imp.flash} shipRef={ship} />
        {wrecking ? (
          <>
            {(["bow", "stern"] as const).map(side => (
              <ShipHalf key={side} side={side} clock={wreck}>
                <ShipBody view={view} />
                <Pieces view={view} artOk={artOk} part={side} />
                <BreakFire side={side} clock={wreck} />
              </ShipHalf>
            ))}
            <Pieces view={view} artOk={artOk} part="sea" />
            <WreckFx clock={wreck} imp={imp} />
          </>
        ) : (
          <>
            <Ship view={view} targets={targets} onSpace={onSpace} storm={mood.storm} rockRef={rock}>
              <Pieces view={view} artOk={artOk} part="ship" />
            </Ship>
            <Pieces view={view} artOk={artOk} part="sea" rock={rock} />
            {actSpace && <ActRing sp={actSpace} rock={rock} />}
          </>
        )}
        {fx.map(f => f.kind === "burst"
          ? <Burst key={f.key} position={f.pos} color={f.color} spread={f.spread} up={f.up} size={f.size} life={f.life} seed={Math.round(f.key * 1000)} count={f.count} additive={f.additive} />
          : <RitualPillar key={f.key} pos={f.pos} />)}
      </Shaker>
    </>
  );
}

/** A pulsing ring under the Deep One that is activating (rides the deck when on board). */
function ActRing({ sp, rock }: { sp: uf.SpaceId; rock: React.RefObject<THREE.Group> }) {
  const ref = useRef<THREE.Group>(null);
  const mat = useRef<THREE.MeshBasicMaterial>(null);
  const [x, z] = monsterAnchor(sp);
  const water = isWater(sp);
  const base = useMemo(() => new THREE.Vector3(x, water ? 0.35 : kDeckY + 0.05, z), [x, z, water]);
  useFrame(({ clock }) => {
    const g = ref.current;
    if (!g) return;
    const t = clock.elapsedTime;
    g.position.copy(base);
    if (!water && rock.current) g.position.applyMatrix4(rock.current.matrix);
    g.scale.setScalar(1 + 0.18 * Math.sin(t * 7));
    if (mat.current) mat.current.opacity = 0.55 + 0.35 * Math.sin(t * 7);
  });
  return (
    <group ref={ref}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.95, 1.25, 48]} />
        <meshBasicMaterial ref={mat} color="#7dffb6" transparent depthWrite={false} toneMapped={false} fog={false} side={THREE.DoubleSide} />
      </mesh>
      <pointLight color="#5fffb0" intensity={6} distance={4} position={[0, 0.8, 0]} />
    </group>
  );
}

export function Scene3D({ viewReset = 0, ...props }: Scene3DProps) {
  return (
    <Canvas shadows dpr={[1, 1.75]} camera={{ position: kCamPos.toArray(), fov: 42, near: 0.5, far: 5000 }}
      gl={{ antialias: true }} onCreated={({ gl }) => { gl.localClippingEnabled = true; }} style={{ position: "absolute", inset: 0 }}>
      <Suspense fallback={null}>
        <World {...props} />
      </Suspense>
      <CameraReset n={viewReset} />
      <OrbitControls target={kCamTarget.toArray()} enableDamping dampingFactor={0.08} minDistance={9} maxDistance={48}
        minPolarAngle={0.25} maxPolarAngle={1.38} panSpeed={0.6} makeDefault />
    </Canvas>
  );
}
